/**
 * SOW Completeness checker.
 * Returns which fields are unassessed so Mo can ask about them
 * and the UI can show section-level status badges.
 */

const ROOM_TYPE_LABELS = {
  master_bedroom: 'Master Bedroom', bedroom: 'Bedroom',
  master_bath: 'Master Bath',       bathroom: 'Bathroom',
  kitchen: 'Kitchen',               living: 'Living Room',
  dining: 'Dining Room',            laundry: 'Laundry',
  hallway: 'Hallway',               garage: 'Garage', other: 'Other',
};

// ── Per-section completeness ──────────────────────────────────────────────────

export function demoCompleteness(demo = {}) {
  const missing = [];
  // If the section was never opened, demo is {}
  const opened = Object.keys(demo).length > 0;
  if (!opened) return { status: 'missing', missing: ['Demo/disposal scope not assessed'] };

  // We need at least a deliberate answer: yes to some work, OR notes explaining none needed
  const hasWork = demo.dumpster_count > 0 || demo.full_cleanout || demo.haul_away || demo.demo_labor > 0;
  const hasNotes = demo.notes && demo.notes.trim().length > 0;
  if (!hasWork && !hasNotes) missing.push('Demo: no scope confirmed — add dumpsters, cleanout, or a note if none needed');

  return { status: missing.length ? 'partial' : 'done', missing };
}

export function systemsCompleteness(sys = {}) {
  const missing = [];
  const opened = Object.keys(sys).length > 0;
  if (!opened) return { status: 'missing', missing: ['Major systems not assessed (roof, HVAC, plumbing, electrical, foundation)'] };

  if (!sys.roof_action)       missing.push('Roof: action not set (none/repair/replace)');
  if (!sys.roof_age)          missing.push('Roof: age unknown');
  if (sys.roof_action === 'replace' && !sys.roof_sq_ft) missing.push('Roof: square footage needed for replace estimate');
  if (!sys.hvac_action)       missing.push('HVAC: action not set (none/service/replace)');
  if (!sys.hvac_age)          missing.push('HVAC: age unknown');
  if (sys.hvac_action === 'replace' && !sys.hvac_tons) missing.push('HVAC: tonnage not set');
  if (!sys.plumbing_action)   missing.push('Plumbing: action not set');
  if (!sys.wh_action)         missing.push('Water heater: action not set (keep/replace)');
  if (!sys.panel_action)      missing.push('Electrical panel: action not set');
  if (!sys.wiring_type)       missing.push('Electrical: wiring type not set (copper/aluminum/mixed)');
  if (!sys.foundation_action) missing.push('Foundation: action not set');

  return { status: missing.length === 0 ? 'done' : missing.length <= 3 ? 'partial' : 'missing', missing };
}

export function exteriorCompleteness(ext = {}) {
  const missing = [];
  const opened = Object.keys(ext).length > 0;
  if (!opened) return { status: 'missing', missing: ['Exterior not assessed (windows, siding, paint, gutters)'] };

  if (!ext.window_count)       missing.push('Exterior: window count not set');
  if (!ext.window_action)      missing.push('Exterior: window action not set');
  if (!ext.siding_action)      missing.push('Exterior: siding action not set');
  if (!ext.ext_paint_action)   missing.push('Exterior: paint action not set');
  if (!ext.gutters_action)     missing.push('Exterior: gutters action not set');
  if (ext.pressure_wash == null) missing.push('Exterior: pressure wash — yes or no?');

  return { status: missing.length === 0 ? 'done' : missing.length <= 2 ? 'partial' : 'missing', missing };
}

export function landscapeCompleteness(land = {}) {
  const missing = [];
  const opened = Object.keys(land).length > 0;
  if (!opened) return { status: 'missing', missing: ['Landscape/hardscape not assessed'] };

  if (!land.landscape_scope)  missing.push('Landscape: scope not set (cleanup/refresh/new install)');
  if (land.sod == null)       missing.push('Landscape: sod — yes or no?');
  if (!land.fence_action)     missing.push('Landscape: fence action not set');
  if (!land.driveway_action)  missing.push('Landscape: driveway action not set');

  return { status: missing.length === 0 ? 'done' : missing.length <= 2 ? 'partial' : 'missing', missing };
}

export function roomCompleteness(room) {
  const missing = [];
  const isKitchen = room.room_type === 'kitchen';
  const isBath    = ['bathroom', 'master_bath'].includes(room.room_type);
  const isBed     = ['bedroom', 'master_bedroom'].includes(room.room_type);
  const isLiving  = ['living', 'dining', 'hallway', 'laundry', 'other'].includes(room.room_type);
  const isGarage  = room.room_type === 'garage';

  // Dimensions — strongly preferred
  if (!room.sq_ft && !(room.length_ft && room.width_ft)) {
    missing.push(`${room.name}: dimensions not entered`);
  }

  // Flooring — every room
  if (!room.flooring_condition) missing.push(`${room.name}: flooring condition not set`);
  if (!room.flooring_type && room.flooring_condition === 'replace') missing.push(`${room.name}: flooring type needed`);

  // Paint
  if (room.paint_walls == null)   missing.push(`${room.name}: wall paint — yes or no?`);
  if (room.paint_ceiling == null) missing.push(`${room.name}: ceiling paint — yes or no?`);
  if (room.drywall_damage == null) missing.push(`${room.name}: drywall damage assessment missing`);

  // Doors
  if (room.door_count == null) missing.push(`${room.name}: door count not entered`);
  if (!room.door_condition)    missing.push(`${room.name}: door condition not set`);

  // Electrical
  if (room.outlets    == null) missing.push(`${room.name}: outlet count not entered`);
  if (room.light_fixtures == null) missing.push(`${room.name}: light fixtures count not entered`);

  // AC vents
  if (room.ac_vents == null && !isGarage) missing.push(`${room.name}: AC vent count not entered`);

  // Closet
  if (room.has_closet && room.closet_door_count == null) missing.push(`${room.name}: closet door count not set`);

  // Kitchen-specific
  if (isKitchen) {
    if (!room.cabinet_condition)    missing.push(`${room.name}: cabinet condition not set`);
    if (!room.countertop_type)      missing.push(`${room.name}: countertop type not set`);
    if (!room.sink_condition)       missing.push(`${room.name}: sink condition not set`);
    if (!room.appliance_fridge)     missing.push(`${room.name}: fridge — reuse/replace/new?`);
    if (!room.appliance_stove)      missing.push(`${room.name}: stove — reuse/replace/new?`);
    if (!room.appliance_dishwasher) missing.push(`${room.name}: dishwasher — reuse/replace/new?`);
  }

  // Bathroom-specific
  if (isBath) {
    if (!room.toilet_condition)       missing.push(`${room.name}: toilet condition not set`);
    if (!room.vanity_condition)       missing.push(`${room.name}: vanity condition not set`);
    if (!room.tub_shower_condition)   missing.push(`${room.name}: tub/shower condition not set`);
    if (!room.exhaust_fan_condition)  missing.push(`${room.name}: exhaust fan not assessed`);
    if (!room.vanity_size && room.vanity_condition !== 'replace') {
      // only flag if they're keeping/painting — size matters for cost
    }
  }

  const total = missing.length;
  return {
    status: total === 0 ? 'done' : total <= 3 ? 'partial' : 'missing',
    missing,
  };
}

// ── Full deal completeness ─────────────────────────────────────────────────────

export function computeSOWCompleteness(deal) {
  const sections = {
    demo:      demoCompleteness(deal.sow_demo),
    systems:   systemsCompleteness(deal.sow_systems),
    exterior:  exteriorCompleteness(deal.sow_exterior),
    landscape: landscapeCompleteness(deal.sow_landscape),
  };

  const roomResults = (deal.rooms || []).map(room => ({
    roomId:   room.id,
    roomName: room.name,
    ...roomCompleteness(room),
  }));

  const allMissing = [
    ...sections.demo.missing,
    ...sections.systems.missing,
    ...sections.exterior.missing,
    ...sections.landscape.missing,
    ...roomResults.flatMap(r => r.missing),
  ];

  const totalItems = 4 + (deal.rooms || []).length;
  const doneItems  = [
    sections.demo.status,
    sections.systems.status,
    sections.exterior.status,
    sections.landscape.status,
    ...roomResults.map(r => r.status),
  ].filter(s => s === 'done').length;

  return {
    sections,
    rooms:      roomResults,
    allMissing,
    score:      totalItems ? Math.round((doneItems / totalItems) * 100) : 0,
    isComplete: allMissing.length === 0,
  };
}
